"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defineCollection = exports.cleanObject = exports.getUniqueId = void 0;
const path_to_regexp_1 = require("path-to-regexp");
const getRandomHexString = (length) => {
    return Array.from(crypto.getRandomValues(new Uint8Array(length)))
        .map((d) => d.toString(16).padStart(2, "0"))
        .join("");
};
const getUniqueId = () => {
    return `${getRandomHexString(16)}`;
};
exports.getUniqueId = getUniqueId;
const cleanObject = (value) => {
    if (!value || typeof value !== "object") {
        return;
    }
    Object.keys(value).forEach((key) => {
        if (value[key] === undefined || Number.isNaN(value[key])) {
            // eslint-disable-next-line no-param-reassign
            delete value[key];
        }
        else if (typeof value[key] === "object") {
            (0, exports.cleanObject)(value[key]);
        }
    });
    return value;
};
exports.cleanObject = cleanObject;
/* eslint-disable @typescript-eslint/ban-types */
class CollectionDefinition {
    constructor(params) {
        this.collectionName = params.collectionName;
        this.idKey = params.idKey;
        this.additionalKeys = params.additionalKeys;
        this.pathKeys = params.pathKeys;
        this.queryDefs = params.queryDefs;
        this.parent = params.parent;
        this.onGenerateIdKey = params.onGenerateIdKey || (() => (0, exports.getUniqueId)());
        this.defaultValue = params.defaultValue;
        this.onAccess = params.onAccess || ((o) => o);
        this.onGenerate = params.onGenerate || ((o) => o);
        this.onWrite = params.onWrite || ((o) => o);
        this.onRead = params.onRead || ((o) => o);
        this.keysToSave = params.keysToSave || [];
        this.audit = params.audit || false;
        this.docKeys = [this.idKey, ...this.pathKeys];
        this.keysToRemove = this.docKeys.filter((key) => !this.keysToSave.includes(key));
        this.constraintKeys = [...this.docKeys, ...this.additionalKeys];
        this.parseDocPath = (() => {
            const parametrizedKeys = Object.fromEntries(this.docKeys.map((key) => [key, `:${key}`]));
            const matchFunc = (0, path_to_regexp_1.match)(this.docPath(parametrizedKeys));
            return (path) => {
                const result = matchFunc(path);
                if (result === false) {
                    return false;
                }
                else {
                    return result.params;
                }
            };
        })();
    }
    basePath(params) {
        if (this.parent) {
            const docPath = this.parent.docPath(params);
            return docPath !== false ? `${docPath}/` : false;
        }
        else {
            return "";
        }
    }
    collectionPath(params) {
        const basePath = this.basePath(params);
        return basePath !== false ? `${basePath}${this.collectionName}` : false;
    }
    docPath(params) {
        const basePath = this.basePath(params);
        const idKey = params[this.idKey];
        return basePath !== false && idKey
            ? `${basePath}${this.collectionName}/${idKey}`
            : false;
    }
    compareConstraint(obj1, obj2) {
        return this.constraintKeys.every((key) => !obj2[key] || obj1[key] === obj2[key]);
    }
    mergeConstraint(data, constraint) {
        const documentData = Object.assign(Object.assign({}, constraint), data);
        if (!this.compareConstraint(documentData, constraint)) {
            console.error("unsatisfied constraint", documentData, constraint);
            throw new Error("unsatisfied constraint");
        }
        return documentData;
    }
    toFirestore(data) {
        const documentData = Object.assign({}, data);
        for (const key of this.keysToRemove) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete documentData[key];
        }
        (0, exports.cleanObject)(documentData);
        return documentData;
    }
    fromFirestore(documentData, path) {
        const data = Object.assign(Object.assign({}, documentData), this.parseDocPath(path));
        return this.onRead(data);
    }
}
const extractContraintKey = (constraint) => {
    return (typeof constraint === "string"
        ? [constraint]
        : constraint.docKeys);
};
const defineCollection = (params) => {
    const { collectionName, idKey, constraints, parent, queryDefs, defaultValue, onGenerateIdKey, onAccess, onGenerate, onWrite, onRead, audit, keysToSave, } = params;
    const additionalKeys = (constraints
        ? [].concat(...constraints.map((constraint) => extractContraintKey(constraint)))
        : []);
    const pathKeys = ((parent === null || parent === void 0 ? void 0 : parent.docKeys) || []);
    return new CollectionDefinition({
        collectionName,
        idKey,
        additionalKeys,
        pathKeys,
        queryDefs,
        parent,
        keysToSave,
        defaultValue,
        onGenerateIdKey,
        onAccess,
        onGenerate,
        onWrite,
        onRead,
        audit,
    });
};
exports.defineCollection = defineCollection;
